import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Loader2 } from "lucide-react"

import type {
  MemorySupplierHint,
  SupplierNameSuggestion,
} from "../../../../../api/types"
import { MemorySuggestion } from "./MemorySuggestion"
import { SupplierAssignControl } from "./SupplierAssignControl"

export interface SupplierGateProps {
  declId: string
  supplierMentioned: string | null
  supplierName: string
  /** True while the post-assign re-extraction is in flight — see
   *  `useSupplierGateAssign`'s own "THE LATCH" doc. Renders the calm
   *  recompute wait instead of the picker; the picker never comes back once
   *  this is true (the supplier is already bound, only the recompute is
   *  still running). */
  recomputing: boolean
  onAssigned: (supplierId: string) => void
  /** Turnus Memory (#2): what a person previously resolved this printed name to. Null
   *  when memory has nothing to say, which is the normal case and renders as the gate
   *  looked before memory existed. */
  memoryHint?: MemorySupplierHint | null
  /** MC_SUPPLIER_MATCH_STRICT: registry suppliers with a similar name, which the strict
   *  match did not bind. Empty (the default) renders the gate as it was. */
  nameSuggestions?: SupplierNameSuggestion[]
}

/**
 * THE MANDATORY FIRST STEP (product owner, 2026-09-04): a declaration that
 * reached review with no supplier bound (`supplierId` null — an ERP export
 * names no issuer, or the pipeline's own match missed) cannot have its
 * article scope or verdicts computed yet, so showing statements at that
 * point would be misleading. This replaces the review mask ENTIRELY (never
 * alongside it, unlike the old chip+banner treatment) until a supplier is
 * on file — the reviewer sees the document (still in the pane beside this
 * one) and this step, nothing else, until it is resolved.
 *
 * Reuses `SupplierAssignControl` (search + create, already wired to the
 * assign endpoint and its own error handling) rather than a new picker —
 * only the trigger and the surrounding copy are new. `SupplierPicker.tsx`
 * (v1's own control) is a narrower search-only combo with no create flow;
 * this gate needs the create path for exactly the ERP-export case that
 * produces a supplier-less declaration in the first place, so
 * `SupplierAssignControl` is the fit.
 */
export function SupplierGate({
  declId,
  supplierMentioned,
  supplierName,
  recomputing,
  onAssigned,
  memoryHint,
  nameSuggestions = [],
}: SupplierGateProps) {
  const { translate } = useTranslation()

  if (recomputing) {
    return (
      <div
        role="status"
        className="flex h-full flex-col items-center justify-center gap-2 bg-card px-6 text-center text-[12px] text-grey5"
      >
        <Loader2 size={16} className="animate-spin" aria-hidden="true" />
        <p className="text-[13px] font-semibold text-white">
          {translate("MC_DECL_V2_SUP_GATE_RECOMPUTE_TITLE")}
        </p>
        <p className="max-w-[320px]">
          {translate("MC_DECL_V2_SUP_GATE_RECOMPUTE_BODY")}
        </p>
        <p className="sv2-gate sv2-italic max-w-[320px]">
          {translate("MC_DECL_V2_SUP_GATE_RECOMPUTE_HINT")}
        </p>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 bg-card px-6 text-center">
      <h2 className="text-[15px] font-semibold text-white">
        {translate("MC_DECL_V2_SUP_GATE_HEADLINE")}
      </h2>
      <p className="max-w-[340px] text-[12px] text-grey5">
        {translate("MC_DECL_V2_SUP_GATE_REASON")}
      </p>
      <SupplierAssignControl
        declId={declId}
        supplierMentioned={supplierMentioned}
        supplierName={supplierName}
        onAssigned={onAssigned}
        trigger={
          <button type="button" className="sv2-btn sv2-btn-primary sv2-btn-sm">
            {translate("MC_DECL_V2_SUP_ASSIGN_CTA")}
          </button>
        }
      />
      {/* ADVISORY ONLY. It names what this printed supplier meant last time; the
          assignment itself is made above, in the picker, by a person. Shown whenever
          memory has something, with no second condition — there is no longer a write
          handler whose absence could silently hide it. */}
      {memoryHint ? (
        <MemorySuggestion declId={declId} hint={memoryHint} />
      ) : null}
      {/* ADVISORY ONLY, after memory, like it: names the master data holds that resemble
          the printed one. The strict match no longer binds them, so a person checks and
          picks in the picker above. */}
      {nameSuggestions.length > 0 ? (
        <div
          className="flex max-w-[360px] flex-col gap-1 rounded-md border border-grey4 bg-grey2 px-3 py-2 text-left"
          data-testid="supplier-name-suggestions"
        >
          <p className="text-[11px] tracking-wide text-grey5 uppercase">
            {translate("MC_DECL_V2_SUP_GATE_SIMILAR_TITLE")}
          </p>
          <ul className="flex flex-col gap-[2px]">
            {nameSuggestions.map((s) => (
              <li
                key={s.supplierId}
                className="text-[13px] font-semibold text-white"
              >
                {s.name}
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-grey5">
            {translate("MC_DECL_V2_SUP_GATE_SIMILAR_HINT")}
          </p>
        </div>
      ) : null}
    </div>
  )
}
